// JSX port of the companion's renderBPChart + renderCategoryDoughnut. Faithful
// react-chartjs-2 replica of the phone's BP doughnut (category distribution + a
// baseline/latest/target comparison row). Reuses the clinically-governed classifyBp
// (bpReadings.js, cross-checked by static s04 — NEVER a second copy of the thresholds)
// and pairBpReadings. Time-series comes from useMeasureReadings (state/data hook), the
// same source BpReadingsCard uses; scalars/targets from the passed view props.
// Chart colors are dark-theme constants (canvas can't read CSS; no window.__ACC_LIGHT).

import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { useMeasureReadings, usePatientRecord } from '@/state/data/patient';
import useNow from '@/hooks/useNow';
import { bpSessionOf, bpSessionCountLabel } from '@/logic/bpSession';
import { measureWindowYmd } from '@/logic/measureWindow';
import { pairBpReadings, classifyBp, BP_CLASS_LABELS, BP_DISPLAY_ORDER, emptyBpBuckets } from '@/logic/bpReadings';
import { getGoalTarget, num, trendArrow, trendLabel } from '@/logic/patientHome';
import { planTargetNum } from '@/logic/carePlanTargets';

ChartJS.register(ArcElement, Tooltip, Legend);

// Dark-theme canvas constants. Slice order + labels come from the shared logic so a
// new protocol band can't be silently dropped out of the count here.
const BP_CLS = {
  hypotension: { color: '#60A5FA', label: BP_CLASS_LABELS.hypotension },
  controlled: { color: '#22C55E', label: BP_CLASS_LABELS.controlled },
  elevated: { color: '#A3E635', label: BP_CLASS_LABELS.elevated },
  stage2: { color: '#EA580C', label: BP_CLASS_LABELS.stage2 },
  crisis: { color: '#DC2626', label: BP_CLASS_LABELS.crisis },
  emergency: { color: '#991B1B', label: BP_CLASS_LABELS.emergency },
};

const CHART_TOOLTIP = {
  backgroundColor: '#2D2B28',
  titleColor: '#FFFFFF',
  bodyColor: '#DDD9D5',
  borderColor: 'rgba(255,255,255,0.1)',
  borderWidth: 1,
  padding: 10,
  titleFont: { family: 'Aeonik', size: 14 },
  bodyFont: { family: 'Aeonik', size: 15 },
  displayColors: true,
  boxWidth: 8,
  boxHeight: 8,
};

function SectionShell({ trend, children }) {
  return (
    <section className="acc-section fade-up" aria-labelledby="ph-bp-title">
      <div className="acc-section__header">
        <h2 className="acc-section__title" id="ph-bp-title">
          Blood Pressure
          {trend && <span className={`acc-section__badge acc-section__badge--${trend}`}>{trendArrow(trend)} {trendLabel(trend)}</span>}
        </h2>
      </div>
      <div>{children}</div>
    </section>
  );
}

export default function BpChart({ userid, carePlan, measureStates }) {
  const { startdateymd, enddateymd } = useMemo(() => measureWindowYmd(30), []);
  const query = useMeasureReadings(userid, '9,10', { startdateymd, enddateymd });
  const rawReadings = query.data?.readings ?? null;
  const paired = useMemo(() => (rawReadings ? pairBpReadings(rawReadings) : []), [rawReadings]);
  const now = useNow();
  const record = usePatientRecord(userid);

  const states = measureStates || [];
  const bpSys = states.find((m) => m.measure_type === 'bp_systolic');
  const bpDia = states.find((m) => m.measure_type === 'bp_diastolic');
  const trend = bpSys?.trend_direction || null;

  if (query.isLoading) {
    return <SectionShell trend={trend}><div className="acc-chart-card" style={{ padding: '20px 16px', textAlign: 'center' }}><p style={{ fontSize: 15 }}>Loading readings…</p></div></SectionShell>;
  }
  if (paired.length === 0) {
    return (
      <SectionShell trend={trend}>
        <div className="acc-chart-card" style={{ padding: '20px 16px', textAlign: 'center' }}>
          <p className="acc-empty-note">Waiting for the first BP reading</p>
          <p style={{ fontSize: 14, margin: '6px 0 0', color: 'var(--text-dim50)' }}>The BPM Pro 2 feeds this once used</p>
        </div>
      </SectionShell>
    );
  }

  const latest = paired[paired.length - 1];
  const avgSys = Math.round(paired.reduce((s, r) => s + r.systolic, 0) / paired.length);
  const avgDia = Math.round(paired.reduce((s, r) => s + r.diastolic, 0) / paired.length);
  // Reveal the baseline ONLY once it's the official CMS-submitted value — WF37 stamps
  // bp measure_state.status='reportable' at submission. Before that (incl. the rolling
  // pre-submission value) show "Establishing", never a provisional number.
  const bpLocked = bpSys?.status === 'reportable';
  const hasBpBaseline = bpLocked && num(bpSys?.baseline_value) != null && num(bpDia?.baseline_value) != null;
  const baselineSys = hasBpBaseline ? Math.round(num(bpSys.baseline_value)) : null;
  const baselineDia = hasBpBaseline ? Math.round(num(bpDia.baseline_value)) : null;
  // The same rule that closes the patient's baseline task: one day with 3 readings in the last 15.
  const session = bpSessionOf(paired, { nowMs: now, reportKind: 'baseline', timeZone: record.data?.timezone ?? null });
  // Plan first — the care plan is the only author of a target, and this row prints the
  // number verbatim as "<130/80". The `care_goal` row behind getGoalTarget is a retired
  // cache that had drifted from the plan on 64 of 67 open care periods, and WF1 stopped
  // writing it on 2026-08-26, so it is empty for every patient enrolled since. The
  // 130/80 constants stay as the LAST resort: a missing target is safe to default, a
  // wrong one is not — and a CKM/KDIGO plan sets 120.
  const planTargets = carePlan?.targets;
  const targetSys = planTargetNum(planTargets, 'bp_systolic') ?? getGoalTarget(carePlan?.goals, 'bp_systolic', 130);
  const targetDia = planTargetNum(planTargets, 'bp_diastolic') ?? getGoalTarget(carePlan?.goals, 'bp_diastolic', 80);

  const buckets = emptyBpBuckets();
  paired.forEach((r) => { buckets[classifyBp(r.systolic, r.diastolic)] += 1; });
  const active = BP_DISPLAY_ORDER.filter((k) => buckets[k] > 0);
  const total = active.reduce((s, k) => s + buckets[k], 0);

  const data = {
    labels: active.map((k) => BP_CLS[k].label),
    datasets: [{
      data: active.map((k) => buckets[k]),
      backgroundColor: active.map((k) => BP_CLS[k].color),
      borderColor: 'rgba(255,255,255,0.08)',
      borderWidth: 2,
      hoverOffset: 4,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: { ...CHART_TOOLTIP, callbacks: { label: (item) => ` ${item.label}: ${item.raw} (${Math.round((item.raw / total) * 100)}%)` } },
    },
  };

  return (
    <SectionShell trend={trend}>
      <div className="acc-chart-card">
        <div className="acc-chart-card__header">
          <div>
            <div className="acc-chart-card__value">{avgSys}/{avgDia}<span className="acc-chart-card__unit">mmHg</span></div>
            <div className="acc-chart-card__delta acc-chart-card__delta--neutral">30-day average</div>
          </div>
        </div>
        <div className="acc-chart-card__doughnut-row">
          <div className="acc-chart-card__doughnut">
            <Doughnut data={data} options={options} />
            <div className="acc-chart-card__doughnut-center">
              <span className="acc-chart-card__doughnut-count">{paired.length}</span>
              <span className="acc-chart-card__doughnut-caption">readings</span>
            </div>
          </div>
          <div className="acc-chart-card__legend">
            {active.map((k) => (
              <div key={k} className="acc-chart-card__legend-row">
                <span className="acc-chart-card__legend-dot" style={{ background: BP_CLS[k].color }} />
                <span className="acc-chart-card__legend-count">{buckets[k]}</span>
                <span className="acc-chart-card__legend-label">{BP_CLS[k].label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="acc-chart-card__comparison">
          <div className="acc-chart-card__compare-item">
            <div className="acc-chart-card__compare-label">Baseline</div>
            {hasBpBaseline ? (
              <div className="acc-chart-card__compare-value">{baselineSys}/{baselineDia}</div>
            ) : (
              <>
                <div className="acc-chart-card__compare-value acc-chart-card__compare-value--pending">Establishing</div>
                <div className="acc-chart-card__compare-sub">{session.ok ? 'Awaiting care team' : bpSessionCountLabel(session.found)}</div>
              </>
            )}
          </div>
          <div className="acc-chart-card__compare-item">
            <div className="acc-chart-card__compare-label">Latest</div>
            <div className="acc-chart-card__compare-value">{latest.systolic}/{latest.diastolic}</div>
          </div>
          <div className="acc-chart-card__compare-item">
            <div className="acc-chart-card__compare-label">Target</div>
            <div className="acc-chart-card__compare-value">&lt;{targetSys}/{targetDia}</div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
